import { Link } from "react-router-dom";
import { Award, FlaskConical, Shield, Users, ArrowRight, Microscope, Target } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function About() {
  return (
    <div className="grid-bg min-h-screen">
      {/* Header */}
      <div
        className="relative hero-overlay py-24"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8533144/pexels-photo-8533144.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <span
            className="text-[11px] tracking-[0.2em] uppercase block mb-2"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            About Us
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Advancing Research <span className="neon-text-cyan">Through Precision</span>
          </h1>
          <p className="text-base md:text-lg max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Apollo Compounds is a US-based research peptide supplier committed to providing the highest quality compounds for scientific discovery and laboratory research.
          </p>
        </div>
      </div>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span
              className="text-[11px] tracking-[0.2em] uppercase block mb-2"
              style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
            >
              Our Mission
            </span>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Empowering Scientific Discovery
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
              At Apollo Compounds, we believe that groundbreaking research begins with exceptional materials. Our mission is to supply the scientific community with premium-grade research peptides and compounds that meet the most rigorous standards of purity and consistency.
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
              Every compound in our catalog undergoes comprehensive analytical verification, including High-Performance Liquid Chromatography (HPLC) and Mass Spectrometry (MS) analysis, ensuring that researchers can trust the integrity of their experimental materials.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              As a US-based operation, we maintain full control over our supply chain, storage conditions, and shipping protocols to preserve compound stability from synthesis to delivery.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Microscope, title: "HPLC Verified", desc: "Every batch tested for purity confirmation" },
              { icon: Shield, title: ">99% Purity", desc: "Exceeding industry quality benchmarks" },
              { icon: Target, title: "Precision Synthesis", desc: "Advanced SPPS manufacturing methods" },
              { icon: FlaskConical, title: "Research-Grade", desc: "Exclusively for scientific investigation" },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="neon-border rounded-lg p-6 card-hover"
                style={{ background: 'var(--bg-surface)' }}
              >
                <Icon className="w-6 h-6 mb-3" style={{ color: 'var(--neon-cyan)' }} />
                <h3 className="text-sm font-bold mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{title}</h3>
                <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

      {/* Our Story */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl">
          <span
            className="text-[11px] tracking-[0.2em] uppercase block mb-2"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            Our Story
          </span>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Why We Started <span className="neon-text-cyan">Apollo Compounds</span>
          </h2>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            We founded Apollo Compounds because we were tired of watching other suppliers overcharge for the same research-grade products. Too many companies in this space exploit the demand for quality peptides by inflating prices far beyond what is reasonable, putting critical research materials out of reach for independent researchers, smaller labs, and academic institutions operating on tight budgets.
          </p>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            We believe that every researcher, regardless of income level or institutional backing, should have access to the compounds they need to conduct meaningful research at a price point they can afford. Scientific discovery should not be gatekept by inflated margins and unnecessary markups.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            That conviction is what drives everything we do. By keeping our operations lean and our pricing fair, we are able to offer the same &gt;99% purity, HPLC-verified compounds that larger suppliers charge a premium for, at prices that make research accessible to everyone. Quality research materials should be a right, not a luxury.
          </p>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <span
            className="text-[11px] tracking-[0.2em] uppercase block mb-2"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            Our Values
          </span>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            What Sets Us Apart
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Award,
              title: "Uncompromising Quality",
              desc: "Quality is not negotiable. Each compound is manufactured using industry-leading solid-phase peptide synthesis (SPPS) methods and verified through multiple analytical techniques. Our Certificates of Analysis document every quality metric for complete transparency.",
            },
            {
              icon: Users,
              title: "Researcher-Focused Service",
              desc: "We understand the demands of research timelines. Our team provides responsive support, detailed product documentation, and flexible ordering options to accommodate the unique needs of laboratories, academic institutions, and research organizations.",
            },
            {
              icon: Shield,
              title: "Regulatory Compliance",
              desc: "Apollo Compounds operates in strict compliance with applicable regulations. All products are clearly designated for research use only, with proper labeling, safety documentation, and handling guidelines provided with every order.",
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="neon-border rounded-lg p-8 card-hover"
              style={{ background: 'var(--bg-surface)' }}
            >
              <Icon className="w-8 h-8 mb-5" style={{ color: 'var(--neon-cyan)' }} />
              <h3
                className="text-xl font-bold mb-3"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                {title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24"
        style={{ background: 'var(--bg-surface)', borderTop: '1px solid rgba(0,240,255,0.08)' }}
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight mb-4"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Partner With Apollo Compounds
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
            Whether you represent a university laboratory, private research institution, or biotech company, we are ready to support your research needs with premium-grade compounds and dedicated service.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/contact" data-testid="about-contact-btn" className="btn-neon px-8 py-3 rounded text-sm inline-flex items-center gap-2">
              GET IN TOUCH <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/products" className="btn-ghost-neon px-8 py-3 rounded text-sm">
              VIEW CATALOG
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
